import { useEffect, useState } from "react";
import {
  Tag as TagIcon,
  FolderTree,
  UserCheck,
  Plus,
  Edit2,
  Trash2,
  Check,
  Search,
  ExternalLink,
  Globe,
  Mail,
  Shield,
  Layers,
  FileText,
  X,
  Eye,
  AlertCircle
} from "lucide-react";
import { LoadingState, ErrorState } from "../../components/LoadingState";
import { EmptyState } from "../../components/common/EmptyState";
import { apiGet, apiSend } from "../../services/api";
import type { Category, Tag, Author } from "../../types";

type AuthorRecord = Author & {
  status?: string;
  website?: string | null;
  social_links?: Record<string, string> | null;
};

export function TaxonomyPage() {
  const [activeTab, setActiveTab] = useState<"categories" | "tags" | "authors">("categories");
  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [authors, setAuthors] = useState<AuthorRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Modals state
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    id: 0,
    name: "",
    slug: "",
    description: "",
    display_order: 0,
    active: true,
    seo_title: "",
    seo_description: ""
  });

  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [tagForm, setTagForm] = useState({
    id: 0,
    name: "",
    slug: "",
    description: ""
  });

  const [isAuthorModalOpen, setIsAuthorModalOpen] = useState(false);
  const [authorForm, setAuthorForm] = useState({
    id: 0,
    name: "",
    slug: "",
    email: "",
    biography: "",
    website: "",
    status: "active"
  });

  const [saving, setSaving] = useState(false);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);
      const [catsRes, tagsRes, authorsRes] = await Promise.all([
        apiGet<Category[]>("/api/categories"),
        apiGet<Tag[]>("/api/tags"),
        apiGet<AuthorRecord[]>("/api/authors")
      ]);
      setCategories(catsRes || []);
      setTags(tagsRes || []);
      setAuthors(authorsRes || []);
    } catch (err: any) {
      setError(err.message || "Failed to load editorial taxonomy");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  // Category Actions
  async function handleSaveCategory(e: React.FormEvent) {
    e.preventDefault();
    try {
      setSaving(true);
      if (categoryForm.id) {
        await apiSend(`/api/categories/${categoryForm.id}`, {
          method: "PATCH",
          body: JSON.stringify(categoryForm)
        });
      } else {
        await apiSend("/api/categories", {
          method: "POST",
          body: JSON.stringify(categoryForm)
        });
      }
      await loadData();
      setIsCategoryModalOpen(false);
    } catch (err: any) {
      alert(err.message || "Failed to save category");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteCategory(id: number, name: string) {
    if (!confirm(`Are you sure you want to delete the category "${name}"? This action is irreversible.`)) return;
    try {
      await apiSend(`/api/categories/${id}`, { method: "DELETE" });
      setCategories((prev) => prev.filter((c) => c.id !== id));
    } catch (err: any) {
      alert(err.message || "Failed to delete category");
    }
  }

  // Tag Actions
  async function handleSaveTag(e: React.FormEvent) {
    e.preventDefault();
    try {
      setSaving(true);
      if (tagForm.id) {
        await apiSend(`/api/tags/${tagForm.id}`, {
          method: "PATCH",
          body: JSON.stringify(tagForm)
        });
      } else {
        await apiSend("/api/tags", {
          method: "POST",
          body: JSON.stringify(tagForm)
        });
      }
      await loadData();
      setIsTagModalOpen(false);
    } catch (err: any) {
      alert(err.message || "Failed to save tag");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteTag(id: number, name: string) {
    if (!confirm(`Are you sure you want to delete tag #${name}?`)) return;
    try {
      await apiSend(`/api/tags/${id}`, { method: "DELETE" });
      setTags((prev) => prev.filter((t) => t.id !== id));
    } catch (err: any) {
      alert(err.message || "Failed to delete tag");
    }
  }

  // Author Actions
  async function handleSaveAuthor(e: React.FormEvent) {
    e.preventDefault();
    try {
      setSaving(true);
      if (authorForm.id) {
        await apiSend(`/api/authors/${authorForm.id}`, {
          method: "PATCH",
          body: JSON.stringify(authorForm)
        });
      } else {
        await apiSend("/api/authors", {
          method: "POST",
          body: JSON.stringify(authorForm)
        });
      }
      await loadData();
      setIsAuthorModalOpen(false);
    } catch (err: any) {
      alert(err.message || "Failed to save author profile");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteAuthor(id: number, name: string) {
    if (!confirm(`Deactivate and archive author profile "${name}"?`)) return;
    try {
      await apiSend(`/api/authors/${id}`, { method: "DELETE" });
      await loadData();
    } catch (err: any) {
      alert(err.message || "Failed to archive author");
    }
  }

  const filteredCategories = categories.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTags = tags.filter((t) =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredAuthors = authors.filter((a) =>
    a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (a.email && a.email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="admin-page-flow">
      {/* Header */}
      <header className="admin-section-header">
        <div>
          <div className="admin-breadcrumb-tag">
            <TagIcon size={13} className="text-[#C99A2E]" />
            <span>EDITORIAL TAXONOMY & BYLINES</span>
          </div>
          <h1 className="admin-title">Taxonomy & Authors</h1>
          <p className="admin-subtitle">
            Manage magazine publication sections, topic keywords, and editorial journalists.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {activeTab === "categories" && (
            <button
              type="button"
              onClick={() => {
                setCategoryForm({
                  id: 0,
                  name: "",
                  slug: "",
                  description: "",
                  display_order: categories.length + 1,
                  active: true,
                  seo_title: "",
                  seo_description: ""
                });
                setIsCategoryModalOpen(true);
              }}
              className="admin-btn-primary"
            >
              <Plus size={16} />
              <span>New Category</span>
            </button>
          )}

          {activeTab === "tags" && (
            <button
              type="button"
              onClick={() => {
                setTagForm({ id: 0, name: "", slug: "", description: "" });
                setIsTagModalOpen(true);
              }}
              className="admin-btn-primary"
            >
              <Plus size={16} />
              <span>New Tag</span>
            </button>
          )}

          {activeTab === "authors" && (
            <button
              type="button"
              onClick={() => {
                setAuthorForm({
                  id: 0,
                  name: "",
                  slug: "",
                  email: "",
                  biography: "",
                  website: "",
                  status: "active"
                });
                setIsAuthorModalOpen(true);
              }}
              className="admin-btn-primary"
            >
              <Plus size={16} />
              <span>New Author Profile</span>
            </button>
          )}
        </div>
      </header>

      {/* Tabs */}
      <div className="flex items-center justify-between border-b border-slate-200">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => { setActiveTab("categories"); setSearchQuery(""); }}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider border-b-2 transition-all ${
              activeTab === "categories"
                ? "border-[#C99A2E] text-[#111318]"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <FolderTree size={16} />
            <span>Categories ({categories.length})</span>
          </button>

          <button
            type="button"
            onClick={() => { setActiveTab("tags"); setSearchQuery(""); }}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider border-b-2 transition-all ${
              activeTab === "tags"
                ? "border-[#C99A2E] text-[#111318]"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <TagIcon size={16} />
            <span>Topic Tags ({tags.length})</span>
          </button>

          <button
            type="button"
            onClick={() => { setActiveTab("authors"); setSearchQuery(""); }}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider border-b-2 transition-all ${
              activeTab === "authors"
                ? "border-[#C99A2E] text-[#111318]"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <UserCheck size={16} />
            <span>Authors & Journalists ({authors.length})</span>
          </button>
        </div>

        {/* Search */}
        <div className="relative w-64">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`Search ${activeTab}...`}
            className="w-full pl-8 pr-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-[#C99A2E]"
          />
        </div>
      </div>

      {/* Main Content Area */}
      {loading ? (
        <LoadingState label="Loading editorial taxonomy data..." />
      ) : error ? (
        <ErrorState message={error} />
      ) : (
        <>
          {/* TAB 1: CATEGORIES */}
          {activeTab === "categories" && (
            <div className="admin-card-panel p-0 overflow-hidden">
              {filteredCategories.length === 0 ? (
                <EmptyState
                  icon={FolderTree}
                  badge="EDITORIAL TAXONOMY"
                  title="No editorial categories found"
                  description={searchQuery ? `No category matching "${searchQuery}".` : "Organize and classify news beats by creating editorial sections."}
                  actionText="Create Category"
                  onAction={() => {
                    setCategoryForm({
                      id: 0,
                      name: "",
                      slug: "",
                      description: "",
                      display_order: categories.length + 1,
                      active: true,
                      seo_title: "",
                      seo_description: ""
                    });
                    setIsCategoryModalOpen(true);
                  }}
                  actionIcon={Plus}
                />
              ) : (
                <div className="admin-table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Category Name</th>
                        <th>Slug & URL</th>
                        <th>Display Order</th>
                        <th>Status</th>
                        <th>Description</th>
                        <th style={{ textAlign: "right" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCategories.map((cat) => (
                        <tr key={cat.id}>
                          <td>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-slate-900 text-sm">{cat.name}</span>
                            </div>
                          </td>
                          <td>
                            <span className="text-xs font-mono text-slate-500">/{cat.slug}</span>
                          </td>
                          <td>
                            <span className="text-xs font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded">
                              #{cat.display_order ?? 0}
                            </span>
                          </td>
                          <td>
                            {cat.active !== false ? (
                              <span className="status-pill status-published">
                                <span className="status-dot" />
                                <span>Active</span>
                              </span>
                            ) : (
                              <span className="status-pill status-draft">
                                <span>Hidden</span>
                              </span>
                            )}
                          </td>
                          <td>
                            <span className="text-xs text-slate-600 max-w-xs truncate block">
                              {cat.description || <span className="italic text-slate-400">None</span>}
                            </span>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <div className="admin-row-actions">
                              <button
                                type="button"
                                title="Edit Category"
                                onClick={() => {
                                  setCategoryForm({
                                    id: cat.id,
                                    name: cat.name,
                                    slug: cat.slug,
                                    description: cat.description || "",
                                    display_order: cat.display_order ?? 0,
                                    active: cat.active !== false,
                                    seo_title: "",
                                    seo_description: ""
                                  });
                                  setIsCategoryModalOpen(true);
                                }}
                                className="action-icon-btn"
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                type="button"
                                title="Delete Category"
                                onClick={() => handleDeleteCategory(cat.id, cat.name)}
                                className="action-icon-btn delete"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: TAGS */}
          {activeTab === "tags" && (
            <div className="admin-card-panel p-0 overflow-hidden">
              {filteredTags.length === 0 ? (
                <EmptyState
                  icon={TagIcon}
                  badge="TOPIC TAGS"
                  title="No topic tags found"
                  description={searchQuery ? `No tags matching "${searchQuery}".` : "Add semantic tags to cross-reference articles across different news beats."}
                  actionText="Create Tag"
                  onAction={() => {
                    setTagForm({ id: 0, name: "", slug: "", description: "" });
                    setIsTagModalOpen(true);
                  }}
                  actionIcon={Plus}
                />
              ) : (
                <div className="admin-table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Tag Name</th>
                        <th>URL Slug</th>
                        <th>Public Link</th>
                        <th style={{ textAlign: "right" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTags.map((tag) => (
                        <tr key={tag.id}>
                          <td>
                            <div className="flex items-center gap-2">
                              <TagIcon size={14} className="text-[#C99A2E]" />
                              <span className="font-bold text-slate-900 text-sm">#{tag.name}</span>
                            </div>
                          </td>
                          <td>
                            <span className="text-xs font-mono text-slate-500">/tag/{tag.slug}</span>
                          </td>
                          <td>
                            <a
                              href={`/tag/${tag.slug}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-[#C99A2E] hover:underline flex items-center gap-1 font-medium"
                            >
                              <span>View Tag Feed</span>
                              <ExternalLink size={12} />
                            </a>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <div className="admin-row-actions">
                              <button
                                type="button"
                                title="Edit Tag"
                                onClick={() => {
                                  setTagForm({
                                    id: tag.id,
                                    name: tag.name,
                                    slug: tag.slug,
                                    description: ""
                                  });
                                  setIsTagModalOpen(true);
                                }}
                                className="action-icon-btn"
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                type="button"
                                title="Delete Tag"
                                onClick={() => handleDeleteTag(tag.id, tag.name)}
                                className="action-icon-btn delete"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* TAB 3: AUTHORS */}
          {activeTab === "authors" && (
            <div className="admin-card-panel p-0 overflow-hidden">
              {filteredAuthors.length === 0 ? (
                <EmptyState
                  icon={UserCheck}
                  badge="NEWSROOM ROSTER"
                  title="No journalists or authors found"
                  description={searchQuery ? `No journalists matching "${searchQuery}".` : "Add journalists, contributors, and columnists with custom bylines and profiles."}
                  actionText="Add Journalist"
                  onAction={() => {
                    setAuthorForm({
                      id: 0,
                      name: "",
                      slug: "",
                      email: "",
                      biography: "",
                      website: "",
                      status: "active"
                    });
                    setIsAuthorModalOpen(true);
                  }}
                  actionIcon={Plus}
                />
              ) : (
                <div className="admin-table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Journalist Name</th>
                        <th>Byline Slug</th>
                        <th>Email & Contact</th>
                        <th>Status</th>
                        <th>Biography</th>
                        <th style={{ textAlign: "right" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAuthors.map((author) => (
                        <tr key={author.id}>
                          <td>
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-xs">
                                {author.name.charAt(0)}
                              </div>
                              <div>
                                <span className="font-bold text-slate-900 text-sm block">{author.name}</span>
                                {author.website && (
                                  <span className="text-[11px] text-slate-400 block">{author.website}</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="text-xs font-mono text-slate-500">/author/{author.slug}</span>
                          </td>
                          <td>
                            <span className="text-xs text-slate-600 font-medium">
                              {author.email || <span className="italic text-slate-400">None</span>}
                            </span>
                          </td>
                          <td>
                            {author.status !== "inactive" ? (
                              <span className="status-pill status-published">
                                <span className="status-dot" />
                                <span>Active</span>
                              </span>
                            ) : (
                              <span className="status-pill status-archived">
                                <span>Inactive</span>
                              </span>
                            )}
                          </td>
                          <td>
                            <span className="text-xs text-slate-600 max-w-sm truncate block">
                              {author.biography || <span className="italic text-slate-400">No biography provided</span>}
                            </span>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <div className="admin-row-actions">
                              <a
                                href={`/author/${author.slug}`}
                                target="_blank"
                                rel="noreferrer"
                                className="action-icon-btn"
                                title="View Author Page"
                              >
                                <ExternalLink size={14} />
                              </a>
                              <button
                                type="button"
                                title="Edit Author"
                                onClick={() => {
                                  setAuthorForm({
                                    id: author.id,
                                    name: author.name,
                                    slug: author.slug,
                                    email: author.email || "",
                                    biography: author.biography || "",
                                    website: author.website || "",
                                    status: author.status || "active"
                                  });
                                  setIsAuthorModalOpen(true);
                                }}
                                className="action-icon-btn"
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                type="button"
                                title="Deactivate Author"
                                onClick={() => handleDeleteAuthor(author.id, author.name)}
                                className="action-icon-btn delete"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* CATEGORY MODAL */}
      {isCategoryModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 border border-slate-200">
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-4">
              <h3 className="font-bold text-slate-900 text-base">
                {categoryForm.id ? "Edit Editorial Category" : "New Editorial Category"}
              </h3>
              <button
                type="button"
                onClick={() => setIsCategoryModalOpen(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveCategory} className="flex flex-col gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">
                  Category Name *
                </label>
                <input
                  type="text"
                  required
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  placeholder="e.g. Technology & AI"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:outline-none focus:border-[#C99A2E]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">
                  URL Slug (Optional - generated automatically)
                </label>
                <input
                  type="text"
                  value={categoryForm.slug}
                  onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value })}
                  placeholder="e.g. technology"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs font-mono focus:outline-none focus:border-[#C99A2E]"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={categoryForm.display_order}
                    onChange={(e) => setCategoryForm({ ...categoryForm, display_order: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:outline-none focus:border-[#C99A2E]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">
                    Navigation Status
                  </label>
                  <select
                    value={categoryForm.active ? "true" : "false"}
                    onChange={(e) => setCategoryForm({ ...categoryForm, active: e.target.value === "true" })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:outline-none focus:border-[#C99A2E]"
                  >
                    <option value="true">Active & Visible</option>
                    <option value="false">Hidden / Draft</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">
                  Editorial Section Description
                </label>
                <textarea
                  rows={2}
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  placeholder="Brief summary of topics covered in this section..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:outline-none focus:border-[#C99A2E]"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200 mt-2">
                <button
                  type="button"
                  onClick={() => setIsCategoryModalOpen(false)}
                  className="px-4 py-2 border border-slate-300 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="admin-btn-primary"
                >
                  {saving ? "Saving..." : "Save Category"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* TAG MODAL */}
      {isTagModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 border border-slate-200">
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-4">
              <h3 className="font-bold text-slate-900 text-base">
                {tagForm.id ? "Edit Keyword Tag" : "New Keyword Tag"}
              </h3>
              <button
                type="button"
                onClick={() => setIsTagModalOpen(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveTag} className="flex flex-col gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">
                  Tag Name *
                </label>
                <input
                  type="text"
                  required
                  value={tagForm.name}
                  onChange={(e) => setTagForm({ ...tagForm, name: e.target.value })}
                  placeholder="e.g. Fintech, Solar Energy, AI"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:outline-none focus:border-[#C99A2E]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">
                  URL Slug
                </label>
                <input
                  type="text"
                  value={tagForm.slug}
                  onChange={(e) => setTagForm({ ...tagForm, slug: e.target.value })}
                  placeholder="e.g. fintech"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs font-mono focus:outline-none focus:border-[#C99A2E]"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200 mt-2">
                <button
                  type="button"
                  onClick={() => setIsTagModalOpen(false)}
                  className="px-4 py-2 border border-slate-300 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="admin-btn-primary"
                >
                  {saving ? "Saving..." : "Save Tag"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* AUTHOR MODAL */}
      {isAuthorModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 border border-slate-200">
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-4">
              <h3 className="font-bold text-slate-900 text-base">
                {authorForm.id ? "Edit Journalist Profile" : "New Journalist Profile"}
              </h3>
              <button
                type="button"
                onClick={() => setIsAuthorModalOpen(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveAuthor} className="flex flex-col gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  value={authorForm.name}
                  onChange={(e) => setAuthorForm({ ...authorForm, name: e.target.value })}
                  placeholder="e.g. Tendai Moyo"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:outline-none focus:border-[#C99A2E]"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">
                    Author Slug
                  </label>
                  <input
                    type="text"
                    value={authorForm.slug}
                    onChange={(e) => setAuthorForm({ ...authorForm, slug: e.target.value })}
                    placeholder="e.g. tendai-moyo"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs font-mono focus:outline-none focus:border-[#C99A2E]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">
                    Editorial Email
                  </label>
                  <input
                    type="email"
                    value={authorForm.email}
                    onChange={(e) => setAuthorForm({ ...authorForm, email: e.target.value })}
                    placeholder="tendai@zimsmart.co.zw"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:outline-none focus:border-[#C99A2E]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">
                  Personal Website or LinkedIn
                </label>
                <input
                  type="url"
                  value={authorForm.website}
                  onChange={(e) => setAuthorForm({ ...authorForm, website: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:outline-none focus:border-[#C99A2E]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">
                  Journalist Biography
                </label>
                <textarea
                  rows={3}
                  value={authorForm.biography}
                  onChange={(e) => setAuthorForm({ ...authorForm, biography: e.target.value })}
                  placeholder="Senior Technology Reporter covering Southern African digital infrastructure..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:outline-none focus:border-[#C99A2E]"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200 mt-2">
                <button
                  type="button"
                  onClick={() => setIsAuthorModalOpen(false)}
                  className="px-4 py-2 border border-slate-300 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="admin-btn-primary"
                >
                  {saving ? "Saving..." : "Save Author"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
